// 2026-04-24: Trang admin quản lý chi tiền thưởng cho NV.
//
// Layout:
//   Header — back về /admin
//   Hero card — tổng đang nợ NV · tổng đã chi · count
//   Filter — toggle "Chưa chi" | "Tất cả"
//   List — mỗi milestone có nút "✅ Đã chi" hoặc "↩ Hủy đánh dấu"
//   Modal note — khi đánh dấu chi, có thể thêm ghi chú (vd: "Trả tiền mặt 24/04")

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { hasPermission } from '../lib/permissions.js';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';

function fmtVnd(v) {
  if (v === null || v === undefined) return '0đ';
  return Number(v).toLocaleString('vi-VN') + 'đ';
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return iso; }
}

function MarkPaidModal({ milestone, onConfirm, onCancel }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm(note.trim() || null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
        <h3 className="text-lg font-semibold mb-2">✅ Xác nhận đã chi tiền thưởng</h3>
        <div className="text-sm text-slate-600 space-y-1 mb-4">
          <div>NV: <strong>{milestone.employee_name}</strong></div>
          <div>Bộ: <strong>{milestone.bo_label}</strong></div>
          <div>Số tiền: <strong className="text-amber-700">{fmtVnd(milestone.reward_amount_vnd)}</strong></div>
        </div>
        <label className="block text-xs font-medium text-slate-700 mb-1">
          Ghi chú (tùy chọn — VD: "Trả tiền mặt 24/04", "Chuyển khoản BIDV"):
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Để trống nếu không cần ghi chú"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
        />
        <div className="flex gap-2 mt-4 justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-2 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 text-slate-700"
          >
            Huỷ
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-50"
          >
            {busy ? 'Đang xác nhận…' : '✅ Xác nhận đã chi'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminRewardsPage() {
  const user = useAuthStore(s => s.user);
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('unpaid'); // unpaid | all
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);  // milestone object đang xác nhận chi

  if (!hasPermission(user, 'rewards')) {
    return <div className="p-6 text-red-600">403 — bạn không có quyền xem trang này.</div>;
  }

  async function load() {
    setLoading(true);
    try {
      const r = await api.adminRewardsList(filter);
      setData(r);
    } catch (e) {
      toast.error('Lỗi tải dữ liệu thưởng: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [filter]);

  async function doMarkPaid(note) {
    if (!confirming) return;
    try {
      await api.adminRewardMarkPaid(confirming.id, note);
      toast.success(`Đã đánh dấu chi ${fmtVnd(confirming.reward_amount_vnd)} cho ${confirming.employee_name}`);
      setConfirming(null);
      load();
    } catch (e) {
      toast.error('Đánh dấu thất bại: ' + (e?.data?.error || e?.message || e));
    }
  }

  async function doUnmarkPaid(milestone) {
    const ok = await showConfirm({
      title: 'Hủy đánh dấu chi',
      message: `Hủy đánh dấu chi cho ${milestone.employee_name} (${milestone.bo_label})?\nDùng khi chấm nhầm.`,
      confirmText: 'Hủy đánh dấu',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await api.adminRewardUnmarkPaid(milestone.id);
      toast.success('Đã hủy đánh dấu');
      load();
    } catch (e) {
      toast.error('Hủy thất bại: ' + (e?.data?.error || e?.message || e));
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b px-4 py-3 flex items-center gap-3">
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Trang admin</Link>
        <h1 className="text-lg font-semibold">🎁 Quản lý tiền thưởng NV</h1>
        <span className="text-xs text-slate-400">2026-04-24</span>
      </header>

      <main className="max-w-5xl mx-auto p-4">
        {loading && <div className="text-sm text-slate-500">Đang tải…</div>}

        {!loading && data && (
          <>
            {/* Hero stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                <div className="text-xs text-rose-600">⏳ Đang nợ NV</div>
                <div className="text-2xl font-bold text-rose-700">
                  {fmtVnd(data.summary.total_unpaid_vnd)}
                </div>
                <div className="text-xs text-rose-500 mt-0.5">
                  {data.summary.unpaid_count} mốc
                </div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="text-xs text-emerald-600">✅ Đã chi</div>
                <div className="text-2xl font-bold text-emerald-700">
                  {fmtVnd(data.summary.total_paid_vnd)}
                </div>
                <div className="text-xs text-emerald-500 mt-0.5">
                  {data.summary.paid_count} mốc
                </div>
              </div>
              <div className="bg-slate-50 border rounded-lg p-4">
                <div className="text-xs text-slate-500">Tổng cộng</div>
                <div className="text-2xl font-bold text-slate-700">
                  {fmtVnd(data.summary.total_unpaid_vnd + data.summary.total_paid_vnd)}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {data.summary.unpaid_count + data.summary.paid_count} mốc
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="text-xs text-amber-600">% Đã chi</div>
                <div className="text-2xl font-bold text-amber-700">
                  {data.summary.paid_count + data.summary.unpaid_count > 0
                    ? Math.round((data.summary.paid_count / (data.summary.paid_count + data.summary.unpaid_count)) * 100)
                    : 0}%
                </div>
              </div>
            </div>

            {/* Filter */}
            <div className="flex gap-2 mb-3">
              {[
                { v: 'unpaid', l: '⏳ Chưa chi', cnt: data.summary.unpaid_count },
                { v: 'all',    l: '🗂️ Tất cả',  cnt: data.summary.unpaid_count + data.summary.paid_count },
              ].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => setFilter(opt.v)}
                  className={`px-3 py-1.5 rounded text-sm font-medium border ${
                    filter === opt.v
                      ? 'bg-brand-600 border-brand-600 text-white'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {opt.l} ({opt.cnt})
                </button>
              ))}
            </div>

            {/* List */}
            <div className="bg-white rounded-lg shadow-sm border">
              {data.items.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">
                  {filter === 'unpaid'
                    ? 'Tuyệt vời! Không có ai đang chờ tiền thưởng. 🎉'
                    : 'Chưa có ai đạt mốc thưởng nào.'}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">NV</th>
                      <th className="text-left px-3 py-2">Bộ</th>
                      <th className="text-right px-3 py-2">Tiền thưởng</th>
                      <th className="text-left px-3 py-2">Ngày đậu</th>
                      <th className="text-left px-3 py-2">Trạng thái</th>
                      <th className="text-right px-3 py-2">Hành động</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((it) => (
                      <tr key={it.id} className="border-t hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{it.employee_name || '—'}</div>
                          <div className="text-xs text-slate-500">
                            {it.shop_code?.toUpperCase()}
                            {it.employee_phone ? ` · ${it.employee_phone}` : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div>{it.bo_label}</div>
                          <div className="text-xs text-slate-500">điểm {it.score}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-amber-700">
                          {fmtVnd(it.reward_amount_vnd)}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {fmtDate(it.achieved_at)}
                        </td>
                        <td className="px-3 py-2">
                          {it.reward_paid === 1 ? (
                            <div>
                              <span className="text-emerald-700 font-medium">✅ Đã chi</span>
                              <div className="text-xs text-slate-500 mt-0.5">
                                {fmtDateTime(it.paid_at)}
                                {it.paid_by_username && ` · ${it.paid_by_username}`}
                              </div>
                              {it.paid_note && (
                                <div className="text-xs text-slate-500 italic mt-0.5">
                                  📝 {it.paid_note}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-rose-600 font-medium">⏳ Chờ chi</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {it.reward_paid === 1 ? (
                            <button
                              onClick={() => doUnmarkPaid(it)}
                              className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                              title="Hủy đánh dấu (nếu chấm nhầm)"
                            >
                              ↩ Hủy đánh dấu
                            </button>
                          ) : (
                            <button
                              onClick={() => setConfirming(it)}
                              className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                            >
                              ✅ Đã chi
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>

      {confirming && (
        <MarkPaidModal
          milestone={confirming}
          onConfirm={doMarkPaid}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
}
